import { store } from "../../store";
import { controlLoading, controlTabBar } from "../../slices/app.slice";
import { config } from "../../../config";
import { GetAccessTokenAPI } from "./accessToken";
import { autoLogin, authenticate } from "../../slices/auth.slice";
import * as service from "../service";

/**
 * Web port of the RN signup API (`apps/uninfo/src/services/api/user/signup.ts`).
 * The backend endpoint keeps its original typo: `sendVerficationCode`.
 */
export const sendEmailVerificationCodeAPI = async (
  username: string,
  email: string,
) => {
  try {
    const resp = await service.call(
      "GET",
      "api/user/signup/sendVerficationCode",
      {
        email,
        username,
      },
      { timeout: 20000 },
    );
    return resp;
  } catch (err) {
    store.dispatch(controlLoading({ showLoading: false }));
    console.error(`sendEmailVerificationCodeAPI ${err}`);
  }
};

/**
 * The backend checks `username` for uniqueness on this same route, so omitting
 * it makes Prisma drop the filter and match the first active user (-20001).
 */
export const resendEmailVerificationCodeAPI = async (
  email: string,
  username: string,
) => {
  // A blank recipient reaches the backend as `email=` and only fails much
  // later inside the mailer, so refuse it here with the same shape the API
  // would return.
  if (!email || !email.trim() || !username || !username.trim()) {
    console.error(
      `resendEmailVerificationCodeAPI aborted: email and username are required, received email ${JSON.stringify(
        email,
      )}, username ${JSON.stringify(username)}`,
    );
    return { result: "Email is required", status: 2 };
  }
  try {
    const resp = await service.call(
      "GET",
      "api/user/signup/sendVerficationCode",
      {
        email,
        username,
      },
      { timeout: 20000 },
    );
    return resp;
  } catch (err) {
    store.dispatch(controlLoading({ showLoading: false }));
    console.error(`resendEmailVerificationCodeAPI ${err}`);
  }
};

export const verifyEmailCodeAPI = async (email: string, code: string) => {
  if (!email || !email.trim() || !code || !code.trim()) {
    return { result: "Email and verification code are required", status: 2 };
  }
  try {
    return await service.call(
      "POST",
      "api/user/signup/verifyEmailCode",
      { email, code },
      { timeout: 20000 },
    );
  } catch (err) {
    store.dispatch(controlLoading({ showLoading: false }));
    console.error(`verifyEmailCodeAPI ${err}`);
  }
};

export const SignupAPI = async (
  username: string,
  email: string,
  phoneNum: string,
  password: string,
  profile: string,
  studentCard: string,
  hkid: string,
  university: string,
  gender: string,
  identity: string,
  yearOfStudy: string,
  termsAccepted?: boolean,
  termsVersion?: string,
  locale?: string,
) => {
  try {
    const payload = {
      username,
      university_email: email,
      phone_num: phoneNum,
      password,
      profile_icon: profile,
      student_card: studentCard,
      hkid_image: hkid,
      university,
      gender,
      identity,
      year_of_study: yearOfStudy,
      terms_accepted: termsAccepted,
      terms_version: termsVersion,
      locale,
    };

    // The backend uploads up to three base64 images to Cloudinary before
    // replying, which regularly exceeds the default 5s request timeout.
    const resp = await service.call(
      "POST",
      "api/user/signup/register",
      payload,
      { timeout: 60000 },
    );

    if (resp.status === 0) {
      const userAuth = resp.result?.user_auth ?? resp.result?.userAuth;
      const normalizedUser = {
        ...resp.result,
        user_auth: userAuth,
      };
      const accessToken = await GetAccessTokenAPI(
        resp.result.id,
        resp.result.university_email,
      );
      store.dispatch(
        autoLogin({
          userType: "student",
          user: normalizedUser,
          access_token: accessToken,
        }),
      );
      config.ACCESS_TOKEN = accessToken;
      store.dispatch(authenticate({ loggedIn: true, user_auth: userAuth }));
      store.dispatch(
        controlTabBar({
          home: true,
          calendar: false,
          survey: false,
          activtiy: false,
          redeem: false,
          qrcodeScan: false,
          chat: false,
          profile: false,
        }),
      );
      store.dispatch(controlLoading({ showLoading: false }));
      return resp;
    }

    store.dispatch(controlLoading({ showLoading: false }));
    return resp;
  } catch (err) {
    store.dispatch(controlLoading({ showLoading: false }));
    console.error(`SignupAPI ${err}`);
  }
};